import { useCompany, useUser } from '@omnipresentgroup/domains';
import { getUserMainRole } from '@omnipresentgroup/helpers';
import type { Company, User } from '@omnipresentgroup/types';
import { useEffect, useMemo } from 'react';
import {
  type IntercomContextValues,
  type IntercomProps,
  useIntercom,
} from 'react-use-intercom';

const getIntercomData = (user: User, company: Company): IntercomProps => {
  const mainRole = getUserMainRole(user.roles);
  return {
    name: user.name,
    email: user.email,
    customAttributes: {
      user_id: user.userId,
      user_type: mainRole.type,
      company: {
        id: company.id,
        name: company.name,
        company_externalID: company.externalId,
      },
    },
  };
};

type UseSetupIntercomProps = {
  enabled: boolean;
  appId?: string;
};

export const useSetupIntercom = (
  properties: UseSetupIntercomProps,
): Omit<IntercomContextValues, 'boot'> => {
  const { enabled, appId } = properties;

  const enabledBoot = enabled && !!appId;

  const { boot, ...others } = useIntercom();

  const { data: user } = useUser();

  const userRole = useMemo(
    () => (user ? getUserMainRole(user.roles) : undefined),
    [user],
  );

  const companyId = useMemo(
    () => (userRole ? userRole.tenantId : undefined),
    [userRole],
  );

  const { data: company } = useCompany({
    id: companyId,
    enabled: !!companyId && enabledBoot,
  });

  useEffect(() => {
    if (user && company && enabledBoot) {
      const intercomInfo = getIntercomData(user, company);
      boot(intercomInfo);
    }
  }, [boot, enabledBoot, user, company]);

  return {
    ...others,
  };
};

export { IntercomProvider } from 'react-use-intercom';
